import type { BorrowingPowerOutput, RepaymentStressOutput } from "./types.js";

// Plain-English result copy for borrowing-power. Centralised in the rules
// package so every surface (Expo result screen, "my results" list, PDF report)
// renders identical wording from one source — which matters for REGULATION, not
// just DRY. Every string here is INFORMATION, not advice (validation §3):
//
//   - No "you can borrow $X" as a verdict — every figure is "an estimate lenders
//     will assess differently — not a pre-approval" (panel lens 2: the
//     misleading-conduct / estimate-as-pre-approval edge under ACL s18 /
//     ASIC-Act s12DB).
//   - The HEM floor is stated as INDICATIVE (the tables are licensed/non-public).
//   - The DTI≥6 cap is surfaced as PORTFOLIO-LEVEL LENDER RATIONING — never
//     "you cannot borrow >6× income" (that hard per-borrower framing is wrong).
//   - No broker/lender/loan-product handoff (the assertNoCreditHandoff guard
//     enforces this on the structured output; the copy never names one either).
//
// Pure string-builders, unit-tested alongside the engine.

const NOT_ADVICE = "Educational tool. Not financial advice.";

/** Whole-dollar AUD with thousands separators. */
export function formatAud(amount: number): string {
  return `A$${Math.round(amount).toLocaleString("en-AU")}`;
}

/** Rate as a percentage, two decimals max. */
function ratePct(fraction: number): string {
  return `${+(fraction * 100).toFixed(2)}%`;
}

// ---------------------------------------------------------------------------
// borrowingPower copy
// ---------------------------------------------------------------------------

/** Headline — explicitly an estimate, NOT a verdict or pre-approval. */
export function borrowingHeadline(out: BorrowingPowerOutput): string {
  return `Indicative borrowing estimate: ${formatAud(out.indicativeMaxLoan)} — an estimate lenders will assess differently, not a pre-approval.`;
}

/** Why the figure is lower than "income × 5" — the buffer made legible. */
export function bufferBullet(out: BorrowingPowerOutput): string {
  return `Lenders assess your repayments at ${ratePct(out.assessmentRate)} — your rate plus the APRA serviceability buffer of ${ratePct(out.bufferApplied)} — not at the rate you'd actually pay. That buffer is the main reason a lender's number is lower than a simple "income × 5" rule of thumb.`;
}

/** The HEM floor, stated as INDICATIVE (licensed, non-public benchmark). */
export function hemBullet(out: BorrowingPowerOutput): string {
  return `Your assessable living expenses are set to ${formatAud(out.appliedAnnualExpenses)} a year — the higher of what you declared and an indicative living-expense floor (HEM) of ${formatAud(out.hemFloorAnnual)}. HEM is an estimate based on published methodology, not a precise figure — the real tables are licensed and not public, so adjust it to your situation.`;
}

/**
 * DTI≥6 surfaced as PORTFOLIO-LEVEL rationing — NEVER a hard per-borrower cap.
 * Returns null when the result isn't in the rationing band.
 */
export function dtiRationingBullet(out: BorrowingPowerOutput): string | null {
  if (!out.dti.inRationingBand) return null;
  const cap = `${+(out.dti.portfolioCapShareOfNewLending * 100).toFixed(0)}%`;
  return `This estimate sits above ${out.dti.rationingThreshold}× your income. From 1 February 2026 APRA limits how much lending each bank can write above ${out.dti.rationingThreshold}× income to ${cap} of its new mortgages — so lenders ration loans at this level. It's a portfolio-level constraint on lenders, not a rule that you personally cannot borrow above ${out.dti.rationingThreshold}× income.`;
}

/** Source + not-advice line. */
export function borrowingSourceBullet(
  settingsVersion: string,
  verifiedAt: string,
): string {
  return `Figures use the APRA serviceability buffer and high-DTI settings in force from ${settingsVersion}, last verified ${verifiedAt}. This is an estimate — lenders assess differently and finalise the actual figure. ${NOT_ADVICE}`;
}

export function borrowingBullets(
  out: BorrowingPowerOutput,
  settingsVersion: string,
  verifiedAt: string,
): string[] {
  const dti = dtiRationingBullet(out);
  return [
    bufferBullet(out),
    hemBullet(out),
    ...(dti ? [dti] : []),
    borrowingSourceBullet(settingsVersion, verifiedAt),
  ];
}

// ---------------------------------------------------------------------------
// repaymentStress copy
// ---------------------------------------------------------------------------

/** Headline — the buffer reframed as the rate you were assessed against. */
export function stressHeadline(out: RepaymentStressOutput): string {
  return `At your current rate your repayment is ${formatAud(out.currentMonthlyRepayment)} a month; at the rate you were assessed against — current plus the APRA ${ratePct(out.bufferApplied)} buffer — it would be ${formatAud(out.bufferedMonthlyRepayment)} a month.`;
}

/** The buffer as personal headroom. */
export function headroomBullet(out: RepaymentStressOutput): string {
  return `That is ${formatAud(out.bufferedExtraMonthly)} more a month. When you were approved, the lender checked you could still meet repayments at this higher rate — so this figure is the headroom you were stress-tested to, not a prediction of where rates will go.`;
}

/** Source + not-advice line. */
export function stressSourceBullet(
  settingsVersion: string,
  verifiedAt: string,
): string {
  return `The buffer is the APRA serviceability buffer in force from ${settingsVersion}, last verified ${verifiedAt}. ${NOT_ADVICE}`;
}

export function stressBullets(
  out: RepaymentStressOutput,
  settingsVersion: string,
  verifiedAt: string,
): string[] {
  return [
    headroomBullet(out),
    stressSourceBullet(settingsVersion, verifiedAt),
  ];
}
